/**
 * @GestionalePD Admin national federation page UI
 */
import { useNavigate } from 'react-router';
import React, { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import {
  Paper,
  TableContainer,
  TableBody,
  Table,
  TableHead,
  Box,
  InputAdornment,
  TablePagination,
  TableFooter,
  TableCell,
  TableRow,
  Container,
  Card,
  useMediaQuery,
  Stack,
  Typography,
  Tooltip,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
//------------------------------------------------------------------------------------------------
import { CustomTextField } from '../../../../utils/StyledInputs';
import { ErrorUI, HttpErrorHandler } from '../../../../utils/ErrorHandler';
import { LoadingUI } from '../../../../utils/LayoutStyles';
import { TablePaginationActions } from '../../../../utils/TableStyles';
import { entityTypeLabel } from '../../../../utils/EntityTypeLabel';
import { CustomIconButton } from '../../../../utils/Buttons';
import API from '../../../../api';

/**
 * @returns Admin national federation page UI
 */
export default function NationalFederation() {
  // general
  const navigate = useNavigate();
  const { state } = useLocation();
  const { federationID } = state;
  const is600Screen = useMediaQuery('(min-width: 600px)');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setLoading] = useState(true);
  const [currentProfile, setCurrentProfile] = useState('');

  // entity data
  const [entities, setEntities] = useState([]);
  const [entityTypeData, setEntityTypeData] = useState({});

  // Table pagination handler
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(is600Screen ? 10 : 5);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - entities.length) : 0;
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // refresh federation
  const refreshFederation = () => {
    setErrorMessage('');

    API.get(`/etypes/${federationID}?expand=entities`)
      .then((response) => {
        setEntities(response.data.entity_type.entities);
        setEntityTypeData(response.data.entity_type);
      })
      .catch((error) => {
        HttpErrorHandler(error, setErrorMessage);
      })
      .finally(() => setLoading(false));
  };

  // search handler
  const [searchMaxLimit, setSearchMaxLimit] = useState(100);

  // search request
  const onSearchRequest = (searchedVal) => {
    const name = searchedVal.target.value;
    setErrorMessage('');

    if (name.length <= searchMaxLimit) {
      API.get(`/etypes/${federationID}?expand=entities&search=${name}`)
        .then((response) => {
          setEntities(response.data.entity_type.entities);

          if (response.data.entity_type.entities.length === 0) {
            setSearchMaxLimit(name.length);
          } else {
            setSearchMaxLimit(name.length + 1);
          }
        })
        .catch((error) => {
          HttpErrorHandler(error, setErrorMessage);
        });
    }
  };

  // initial federation data loading
  useEffect(() => {
    refreshFederation();
  }, []);

  // get user data
  useEffect(() => {
    let data;

    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));

    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
    }
  }, []);

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;

  // if entity id not found redirect to 404 page
  if (!federationID) return <Navigate to={{ pathname: '/404' }} />;

  return !isLoading ? (
    errorMessage.length === 0 ? (
      <Container maxWidth="xl">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          role="presentation"
          sx={{ marginBottom: '24px' }}
        >
          <Typography
            sx={{
              height: '24px',
              width: 'fit-content',
              whiteSpace: 'nowrap',
              borderRadius: '6px',
              padding: '0 10px',
              textAlign: 'center',
              fontWeight: '700',
              fontSize: '1.1em',
            }}
          >
            {entityTypeData.name}
          </Typography>
        </Box>

        <Card sx={{ padding: '24px 24px 6px' }}>
          {/* search filed */}
          <Box sx={{ marginBottom: '20px' }}>
            <CustomTextField
              fullWidth
              size="small"
              id="national-org-search"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              placeholder="Cerca..."
              onChange={onSearchRequest}
            />
          </Box>

          {/* organization table */}
          <TableContainer component={Paper}>
            <Table size="small" sx={{ minWidth: 700 }} aria-label="Federation table">
              {/* organization table header */}
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '200px' }}>Tipo Entità</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell sx={{ width: '300px' }} />
                </TableRow>
              </TableHead>

              {/* organization table body */}
              {entities.length !== 0 ? (
                <TableBody>
                  {(rowsPerPage > 0
                    ? entities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    : entities
                  ).map((entity) => (
                    <TableRow hover key={entity.id}>
                      <TableCell sx={{ width: '200px' }}>{entityTypeLabel(entity, true)}</TableCell>
                      <TableCell>{entity.name}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" justifyContent="end" gap="12px">
                          <Tooltip title="Anagrafica" arrow>
                            <Box>
                              <CustomIconButton
                                onClick={() =>
                                  navigate('/admin/organization/federations/federation-details', {
                                    state: { federationID: entity.id },
                                  })
                                }
                              >
                                <DescriptionOutlinedIcon />
                              </CustomIconButton>
                            </Box>
                          </Tooltip>

                          <Tooltip title="Sottolivelli" arrow sx={{ display: entity.master === 0 ? 'flex' : 'none' }}>
                            <Box>
                              <CustomIconButton
                                onClick={() =>
                                  navigate('/admin/organization/federations/federation-child', {
                                    state: { federationID: entity.id },
                                  })
                                }
                              >
                                <AccountTreeOutlinedIcon />
                              </CustomIconButton>
                            </Box>
                          </Tooltip>

                          <Tooltip title="Iscritti" arrow sx={{ display: entity.master === 1 ? 'flex' : 'none' }}>
                            <Box>
                              <CustomIconButton
                                onClick={() =>
                                  navigate(`/admin/organization/federations/users`, { state: { clubID: entity.id } })
                                }
                              >
                                <PeopleOutlineIcon />
                              </CustomIconButton>
                            </Box>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={3} />
                    </TableRow>
                  )}
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center' }}>
                      Nessuna entità trovata
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}

              {/* organization table footer with pagination */}
              <TableFooter>
                <TableRow>
                  <TablePagination
                    sx={{ borderBottom: 'none' }}
                    rowsPerPageOptions={[]}
                    colSpan={5}
                    count={entities.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{
                      inputProps: {
                        'aria-label': 'organization per page',
                      },
                      native: true,
                    }}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Card>
      </Container>
    ) : (
      ErrorUI(errorMessage)
    )
  ) : (
    LoadingUI()
  );
}
